import React, { useState } from 'react';
import Modal from './Modal';
import axios from 'axios';
import { toast } from 'react-toastify';
import Form from '../Form/Form';
import InputText from '../Form/Fields/InputText';
import Textarea from '../Form/Fields/Textarea';
import InputLaboratory from '../Form/Fields/InputLaboratory';
import Select from '../Form/Fields/Select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPills, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import Button from '../Form/Button';
import { API_BASE_URL } from '../../api';

import './CreateProductModal.scss';

const STATUS_OPTIONS = [
  { value: 'MARKETED', label: 'Commercialisé' },
  { value: 'NOT_MARKETED', label: 'Non commercialisé' },
];

const REFUNDABLE_OPTIONS = [
  { value: 'false', label: 'Non remboursable' },
  { value: 'true', label: 'Remboursable' },
];

function EditProductFormContent() {
  const [showMore, setShowMore] = useState(false);

  return (
    <>
      <div className="cdm-section">
        <p className="cdm-section__label">Informations</p>

        <div className="cdm-grid-2">
          <div>
            <label className="cdm-field-label">Code-barres</label>
            <InputText name="barcode" placeholder="611 ***** *****" />
          </div>
          <div>
            <label className="cdm-field-label">Prix <span className="text-danger">*</span></label>
            <div className="cdm-price-wrapper">
              <InputText name="publicPrice" placeholder="0.00" validationRules={{ required: "Le prix est requis" }} />
              <span className="cdm-price-badge">Dhs</span>
            </div>
          </div>
        </div>

        <div className="cdm-grid-2 mt-2">
          <div>
            <label className="cdm-field-label">Prix hospitalier</label>
            <div className="cdm-price-wrapper">
              <InputText name="hospitalPrice" placeholder="0.00" />
              <span className="cdm-price-badge">Dhs</span>
            </div>
          </div>
          <div>
            <label className="cdm-field-label">Seuil d'alerte stock</label>
            <InputText name="minimumStock" type="number" placeholder="0" />
          </div>
        </div>

        <div className="cdm-grid-2 mt-2">
          <div>
            <label className="cdm-field-label">Présentation</label>
            <InputText name="presentation" placeholder="Comprimé, Sirop..." />
          </div>
          <div>
            <label className="cdm-field-label">Dosage</label>
            <InputText name="dosage" placeholder="20 MG" />
          </div>
        </div>

        <div className="cdm-grid-2 mt-2">
          <div>
            <label className="cdm-field-label">Statut</label>
            <Select name="status" options={STATUS_OPTIONS} placeholder="Choisir un statut..." />
          </div>
          <div>
            <label className="cdm-field-label">Remboursement</label>
            <Select name="refundable" options={REFUNDABLE_OPTIONS} placeholder="Remboursable ?" />
          </div>
        </div>

        <div className="mt-2">
          <label className="cdm-field-label">Laboratoire</label>
          <InputLaboratory name="laboratoryId" />
        </div>
      </div>

      <div className="cdm-section">
        <button type="button" className="cdm-optional-toggle" onClick={() => setShowMore((prev) => !prev)}>
          <FontAwesomeIcon icon={showMore ? faChevronUp : faChevronDown} />
          {showMore ? 'Masquer les informations complémentaires' : 'Informations complémentaires'}
        </button>

        {showMore && (
          <>
            <div className="cdm-grid-2 mt-2">
              <div>
                <label className="cdm-field-label">Famille</label>
                <InputText name="family" placeholder="Famille thérapeutique" />
              </div>
              <div>
                <label className="cdm-field-label">Type produit</label>
                <InputText name="productType" />
              </div>
            </div>

            <div className="cdm-grid-2 mt-2">
              <div>
                <label className="cdm-field-label">Code ATC</label>
                <InputText name="atcCode" />
              </div>
              <div>
                <label className="cdm-field-label">Tableau</label>
                <InputText name="tableLetter" />
              </div>
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Composition</label>
              <Textarea name="composition" placeholder="Composition du produit..." />
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Indication</label>
              <Textarea name="indication" placeholder="Indications thérapeutiques..." />
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Posologie</label>
              <Textarea name="dosagesAdministration" placeholder="Posologie et mode d'administration..." />
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Contre-indications</label>
              <Textarea name="contraindication" placeholder="Contre-indications..." />
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Mises en garde</label>
              <Textarea name="warnings" placeholder="Mises en garde et précautions d'emploi..." />
            </div>

            <div className="cdm-grid-2 mt-2">
              <div>
                <label className="cdm-field-label">Dépendance</label>
                <InputText name="dependanceRisk" />
              </div>
              <div>
                <label className="cdm-field-label">Substance psychoactive</label>
                <InputText name="psychoactiveSubstance" />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default function EditProductModal({ product, show, onClose, onSave }) {
  const [isLoading, setIsLoading] = useState(false);

  if (!show || !product) return null;

  const defaultValues = {
    barcode: product.barcode || '',
    publicPrice: product.publicPrice ?? '',
    hospitalPrice: product.hospitalPrice ?? '',
    minimumStock: product.minimumStock ?? '',
    presentation: product.presentation || '',
    dosage: product.dosage || '',
    status: product.status || 'MARKETED',
    refundable: product.refundable == null ? '' : String(product.refundable),
    laboratoryId: product.laboratory?.id || null,
    family: product.family || '',
    productType: product.productType || '',
    atcCode: product.atcCode || '',
    tableLetter: product.tableLetter || '',
    composition: product.composition || '',
    indication: product.indication || '',
    dosagesAdministration: product.dosagesAdministration || '',
    contraindication: product.contraindication || '',
    warnings: product.warnings || '',
    dependanceRisk: product.dependanceRisk || '',
    psychoactiveSubstance: product.psychoactiveSubstance || '',
  };

  const onSubmit = (data) => {
    setIsLoading(true);
    axios.put(`${API_BASE_URL}/api/v1/products/${product.id}`, {
      barcode: data.barcode,
      publicPrice: data.publicPrice,
      status: data.status || null,
      presentation: data.presentation || null,
      refundable: data.refundable === 'true' ? true : data.refundable === 'false' ? false : null,
      dosage: data.dosage || null,
      laboratoryId: data.laboratoryId || null,
      family: data.family || null,
      productType: data.productType || null,
      hospitalPrice: data.hospitalPrice || null,
      minimumStock: data.minimumStock || null,
      atcCode: data.atcCode || null,
      tableLetter: data.tableLetter || null,
      indication: data.indication || null,
      composition: data.composition || null,
      dosagesAdministration: data.dosagesAdministration || null,
      contraindication: data.contraindication || null,
      warnings: data.warnings || null,
      dependanceRisk: data.dependanceRisk || null,
      psychoactiveSubstance: data.psychoactiveSubstance || null,
    }, { withCredentials: true })
      .then((response) => {
        onClose();
        toast.success('Produit modifié avec succès !');
        onSave?.(response.data);
      })
      .catch((error) => {
        console.error('Error updating product:', error);
        toast.error("Erreur lors de la modification du produit");
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Modal
      id="editProductModal"
      show={show}
      onClose={onClose}
      title={`Modifier — ${product.name}`}
      icon={faPills}
      iconBg="#e0f2fe"
      iconColor="#0284c7"
      footer={
        <Button isLoading={isLoading} loadingMessage="Enregistrement..." type="submit" form="editProductForm" className="btn btn-primary">
          Enregistrer les modifications
        </Button>
      }
    >
      <Form id="editProductForm" onSubmit={onSubmit} defaultValues={defaultValues}>
        <EditProductFormContent />
      </Form>
    </Modal>
  );
}
